import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Skeleton } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';

import { SkeletonBlockComponent } from '../../shared/components/skeleton-block/skeleton-block.component';

import { DomService } from '../../core/services/dom.service';
import { ContadorInforme, ListaService } from '../../core/services/lista.service';

@Component({
  selector: 'app-contador',
  standalone: true,
  imports: [Button, Skeleton, TableModule, DatePipe, SkeletonBlockComponent],
  templateUrl: './contador.component.html',
  styleUrl: './contador.component.scss',
})
export class ContadorComponent implements OnInit {
  private readonly domService = inject(DomService);
  private readonly listaService = inject(ListaService);
  private readonly messages = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly pasos = [1, 2, 5, 10] as const;
  readonly skeletonRows = [0, 1, 2, 3, 4];

  readonly cargando = signal(true);
  readonly enviando = signal(false);
  readonly total = signal(0);
  readonly errorDom = signal<string | null>(null);
  readonly fechaDom = signal<string | null>(null);
  readonly informes = signal<ContadorInforme[]>([]);
  readonly cargandoInformes = signal(false);
  readonly errorInformes = signal<string | null>(null);

  ngOnInit(): void {
    this.cargarDomingo();
  }

  cargarDomingo(): void {
    this.cargando.set(true);
    this.errorDom.set(null);
    this.domService
      .actual()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (dom) => {
          this.fechaDom.set(dom.dom);
          this.cargando.set(false);
          this.cargarInformes();
        },
        error: (err: HttpErrorResponse) => {
          this.fechaDom.set(null);
          this.cargando.set(false);
          this.errorDom.set(this.mensajeDe(err, 'No se pudo cargar el domingo.'));
        },
      });
  }

  cargarInformes(): void {
    this.cargandoInformes.set(true);
    this.errorInformes.set(null);
    this.listaService
      .historialContador()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (informes) => {
          this.informes.set(informes);
          this.cargandoInformes.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.cargandoInformes.set(false);
          this.errorInformes.set(this.mensajeDe(err, 'No se pudo cargar el historial.'));
        },
      });
  }

  fechaVisible(): string {
    const fecha = this.fechaDom();
    if (!fecha) {
      return '';
    }
    const [anio, mes, dia] = fecha.split('-');
    if (!anio || !mes || !dia) {
      return fecha;
    }
    return `${dia}/${mes}/${anio}`;
  }

  sumar(delta: number): void {
    if (this.enviando()) {
      return;
    }
    this.total.update((actual) => Math.max(0, actual + delta));
  }

  enviar(): void {
    const total = this.total();
    if (total <= 0 || this.enviando() || this.cargando() || this.errorDom()) {
      return;
    }

    this.enviando.set(true);
    this.listaService
      .enviarContador(total)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.enviando.set(false);
          this.total.set(0);
          this.cargarInformes();
          this.messages.add({
            severity: 'success',
            summary: 'Informe enviado.',
          });
        },
        error: (err: HttpErrorResponse) => {
          this.enviando.set(false);
          this.messages.add({
            severity: 'error',
            summary: 'No se pudo enviar',
            detail: this.mensajeDe(err, 'No se pudo enviar el informe.'),
          });
        },
      });
  }

  private mensajeDe(err: HttpErrorResponse, fallback: string): string {
    if (err.status === 0) {
      return 'No se pudo contactar al servidor.';
    }
    const message = err.error?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }
}
